import { useState } from 'react';
import { createSimulatorOnDaemon } from '../lib/api';

interface Props {
  onClose: () => void;
  onDone: () => Promise<void> | void;
}

/**
 * "Reattach existing charger" — takes a charger_id that already exists in
 * the main chargers table and opens a WebSocket session for it on the
 * daemon. Used to recover after a daemon redeploy where the in-memory
 * list was reset. The daemon derives the port count from the ports table
 * so the user doesn't have to remember it.
 */
export default function ReattachModal({ onClose, onDone }: Props) {
  const [chargerId, setChargerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createSimulatorOnDaemon(chargerId.trim());
      await onDone();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Reattach failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="modal" onSubmit={submit}>
        <h2>Reattach existing charger</h2>
        <p className="muted" style={{ marginBottom: 16 }}>
          Opens a WebSocket for a charger that already exists in the database. Use this after a daemon
          redeploy to bring simulators back online without recreating them.
        </p>
        <div className="field">
          <label>Charger ID</label>
          <input
            value={chargerId}
            onChange={(e) => setChargerId(e.target.value.toUpperCase())}
            placeholder="e.g. SIMV11000"
            required
          />
        </div>
        <div className="row mt" style={{ gap: 8 }}>
          <button type="submit" disabled={busy || !chargerId.trim()} style={{ flex: 1 }}>
            {busy ? 'Attaching…' : 'Attach'}
          </button>
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
        </div>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}
